import React from "react";
import { Button } from "@/components/ui/button";
import { Download, ShieldCheck } from "lucide-react";

const ROLE_STYLE = {
  BASE: "border-emerald-400 bg-emerald-50 text-emerald-900",
  REPLACEMENT_VALID: "border-emerald-600 bg-emerald-100 text-emerald-950 ring-1 ring-emerald-500",
  SUPERSEDED: "border-gray-300 bg-gray-100 text-gray-500 line-through",
  REPLACEMENT_ATTEMPT: "border-amber-400 bg-amber-50 text-amber-900",
};
const ROLE_LABEL = {
  BASE: "usato nella fusione", REPLACEMENT_VALID: "sostituzione valida (S6R1)",
  SUPERSEDED: "SUPERSEDED — escluso", REPLACEMENT_ATTEMPT: "tentativo",
};

export const SegmentsStrip = ({ segments }) => (
  <div className="flex flex-wrap gap-1" data-testid="result-segments-strip">
    {segments.map((s) => (
      <div key={s.segment_id} title={`${s.state} · POST ${s.send_count} · GET ${s.poll_attempts} · ${s.response_id || ""}`}
        className={`px-1.5 py-0.5 border text-[10px] font-mono ${ROLE_STYLE[s.role] || "border-border"}`}
        data-testid={`result-segment-${s.segment_id}`}>
        <b>{s.display_id}</b>{s.component_code ? ` ${s.component_code.slice(-4)}` : " pacchetto"} · p.{(s.pages || []).join(",")}
        <span className="ml-1 font-sans no-underline">{ROLE_LABEL[s.role]}</span>
      </div>
    ))}
  </div>
);

const VERSION_LABEL = { "P1.7.1": "P1.7.1 (hardened)", "P1.7.2": "P1.7.2 (canonical hardened)", "P1.7.3": "P1.7.3 (Fast)" };
const versionLabel = (v) => { const m = (v || "").match(/P1\.7(\.\d+)?/); return m ? (VERSION_LABEL[m[0]] || m[0]) : (v || "—"); };

export const ResultHeader = ({ d, onExport, exporting }) => {
  const { run, job, cost, integrity, merged_document: md } = d;
  const allOk = Object.values(integrity).every(Boolean);
  const isFast = !!cost.service_tier_requested || Number(cost.sum_fast_premium_usd) > 0;
  const uncertain = (cost.uncertain_exposure_markers || []).filter((m) => !m.resolved);
  const uncertainSum = uncertain.reduce((s, m) => s + Number(m.uncertain_exposure_usd || 0), 0);
  return (
    <div className="border border-border bg-white p-3 space-y-2" data-testid="result-header">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="result-kicker">
            Revisione risultato IA — lettura segmentata {versionLabel(run.reader_version)} · tariffa {cost.service_tier_label || (isFast ? "FAST" : "STANDARD")}
          </div>
          <h1 className="text-lg font-display font-bold truncate" data-testid="result-title">{job.filename}</h1>
          <div className="text-[10px] font-mono text-muted-foreground break-all" data-testid="result-run-id">{run.run_id}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="px-2 py-1 border-2 border-orange-500 bg-orange-50 text-orange-900 text-[11px] font-bold" data-testid="result-review-state">{d.effective_state || d.review_state}</span>
          <span className="text-[10px] text-muted-foreground" data-testid="result-approval-note">Nessuna approvazione disponibile in questa fase</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
        <div>Costo consuntivo run: <b data-testid="result-cost-total">${cost.confirmed_total_usd.toFixed(4)}</b></div>
        <div data-testid="result-cost-breakdown">
          {isFast
            ? <>= standard <b data-testid="result-cost-standard">${cost.sum_standard_usd.toFixed(4)}</b> + premio Fast <b data-testid="result-cost-premium">${cost.sum_fast_premium_usd.toFixed(4)}</b></>
            : <>= Σ standard <b data-testid="result-cost-standard">${cost.sum_standard_usd.toFixed(4)}</b> <span className="text-muted-foreground">(nessun premio Fast)</span></>}
        </div>
        <div>Segmenti nel totale: <span className="font-mono" data-testid="result-cost-segments">{cost.segments_in_total.join(" ")}</span></div>
        <div>Tetto Owner: ${cost.authorized_cap_usd} · esposizione incerta (fuori dal totale): <b data-testid="result-uncertain-exposure">{uncertain.length ? `$${uncertainSum.toFixed(4)} (${uncertain.map((m) => m.segment_id).join(", ")})` : "nessuna"}</b></div>
        <div className="col-span-2 sm:col-span-4 text-[10px] text-muted-foreground" data-testid="result-cost-formula">{cost.formula}</div>
        <div className="col-span-2 sm:col-span-4 flex flex-wrap items-center gap-2">
          <ShieldCheck size={12} className={allOk ? "text-emerald-700" : "text-red-700"} />
          <span data-testid="result-integrity">{allOk ? "Integrità verificata" : "INTEGRITÀ NON VERIFICATA"}: merged_hash ricalcolato {integrity.merged_hash_recomputed_ok ? "OK" : "KO"} · hash run = documento {integrity.run_merged_hash_matches_document ? "OK" : "KO"} · output segmenti {integrity.segment_output_hashes_verified ? "OK" : "KO"} · provenienza componenti {integrity.components_source_verified ? "OK" : "KO"}</span>
          <span className="font-mono text-[9px] text-muted-foreground break-all" data-testid="result-merged-hash">merged_hash {md.merged_hash}</span>
          <span className="text-[9px] text-muted-foreground">fuso il {String(md.created_at).slice(0, 19).replace("T", " ")}Z · {d.merged.reader_version} · {d.merged.merger_version}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentsStrip segments={d.segments} />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onExport} disabled={exporting} data-testid="result-export-json-btn">
          <Download size={12} className="mr-1" />{exporting ? "Esportazione…" : "Esporta JSON reale"}
        </Button>
      </div>
    </div>
  );
};
